import {
  BarChart as RechartsBarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { useRef, useState } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { rowFromChartClick } from '@/lib/chart-click';
import type { PlotSpan } from '@/lib/chart-scrub';
import { useIsMobile } from '@/hooks/use-mobile';
import type { PartialEdges } from '@/lib/partial-buckets';
import { MetricsChartTooltip } from '@/components/ui/charts/chart-tooltip';
import {
  ChartPartialDefs,
  ChartPartialLegend,
  hasPartialEdge,
} from '@/components/ui/charts/chart-partial';
import {
  ChartPlotAreaReporter,
  ChartScrubOverlay,
} from '@/components/ui/charts/chart-scrub-overlay';
import {
  CHART_MARGIN,
  chartGridProps,
  chartXAxisProps,
  chartYAxisProps,
} from '@/components/ui/charts/chart-theme';

export type MetricsBarChartProps = {
  data: Record<string, unknown>[];
  syncId?: string;
  syncMethod?: 'index' | 'value';
  className?: string;
  yAxisWidth?: number;
  yAxisDomain?: ComponentProps<typeof YAxis>['domain'];
  allowDecimals?: boolean;
  tooltipContent?: ComponentProps<typeof MetricsChartTooltip>['content'];
  /**
   * Makes the whole column of a bucket clickable (not just the painted bars,
   * so empty space above a short bar works too). Receives the clicked data row.
   */
  onBucketClick?: (row: Record<string, unknown>) => void;
  /** Index of the row under the finger (mobile) or mouse (desktop); null on release/leave. */
  onActiveIndexChange?: (index: number | null) => void;
  /** Partial edges of `data`; adds the hatched legend item (CHART-04). */
  partialLegend?: PartialEdges;
  children: ReactNode;
};

/**
 * Bar chart shell with shared axis/grid defaults. Pass Recharts `Bar` children.
 *
 * Below 768px the touch reading works like {@link MetricsAreaChart}'s, with
 * one band per row.
 */
export function MetricsBarChart({
  data,
  syncId,
  syncMethod,
  className,
  yAxisWidth = 40,
  yAxisDomain,
  allowDecimals = false,
  tooltipContent,
  onBucketClick,
  onActiveIndexChange,
  partialLegend,
  children,
}: MetricsBarChartProps) {
  const isMobile = useIsMobile();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [readout, setReadout] = useState<HTMLDivElement | null>(null);
  const [plotArea, setPlotArea] = useState<PlotSpan | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const chart = (
    <ResponsiveContainer
      key={isMobile ? 'mobile' : 'desktop'}
      className={cn(
        'min-h-[200px] w-full',
        !isMobile && onBucketClick && '[&_.recharts-wrapper]:cursor-pointer',
        className
      )}
    >
      <RechartsBarChart
        data={data}
        onClick={
          !isMobile && onBucketClick
            ? (state) => {
                const row = rowFromChartClick(state, data);
                if (row) onBucketClick(row);
              }
            : undefined
        }
        onMouseMove={
          !isMobile && onActiveIndexChange
            ? (state) =>
                onActiveIndexChange(
                  rowFromChartClick(state, data)
                    ? Number(state.activeIndex)
                    : null
                )
            : undefined
        }
        onMouseLeave={
          !isMobile && onActiveIndexChange
            ? () => onActiveIndexChange(null)
            : undefined
        }
        margin={CHART_MARGIN}
        syncId={isMobile ? undefined : syncId}
        syncMethod={isMobile ? undefined : syncMethod}
        barCategoryGap={2}
      >
        <ChartPartialDefs />
        <CartesianGrid {...chartGridProps} />
        <XAxis {...chartXAxisProps} />
        <YAxis
          {...chartYAxisProps}
          width={yAxisWidth}
          orientation="right"
          allowDecimals={allowDecimals}
          domain={yAxisDomain}
        />
        {isMobile ? (
          <MetricsChartTooltip
            content={tooltipContent}
            variant="bar"
            portal={readout}
            defaultIndex={activeIndex ?? undefined}
          />
        ) : (
          <MetricsChartTooltip content={tooltipContent} variant="bar" />
        )}
        {isMobile ? <ChartPlotAreaReporter onChange={setPlotArea} /> : null}
        {partialLegend && hasPartialEdge(partialLegend) ? (
          <Legend
            verticalAlign="bottom"
            align="left"
            content={
              <ChartPartialLegend edges={partialLegend} variant="hatch" />
            }
          />
        ) : null}
        {children}
      </RechartsBarChart>
    </ResponsiveContainer>
  );

  if (!isMobile) return chart;

  return (
    <div
      ref={wrapperRef}
      className="relative h-full w-full max-md:scroll-mt-[var(--chart-anchor-offset,0px)]"
    >
      <div
        ref={setReadout}
        data-chart-readout
        className="pointer-events-none absolute inset-x-0 bottom-full z-20"
      />
      {chart}
      <ChartScrubOverlay
        count={data.length}
        scale="band"
        plotArea={plotArea}
        onIndexChange={(index) => {
          setActiveIndex(index);
          onActiveIndexChange?.(index);
        }}
        onTap={
          onBucketClick
            ? (index) => {
                const row = data[index];
                if (!row) return;
                wrapperRef.current?.scrollIntoView({ block: 'start' });
                onBucketClick(row);
              }
            : undefined
        }
      />
    </div>
  );
}
